import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { apiFetch } from "../../services/api";
import { formatDate } from "../../utils/formatters";
import { T } from "../../constants/theme";

const DANGER = "#A94442";

export default function AdminSecurityPage() {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [eventType, setEventType] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPageNum] = useState(1);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "superadmin")) return;
    let mounted = true;
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: "40" });
    if (eventType) params.set("event_type", eventType);
    if (search) params.set("search", search);

    apiFetch(
      `/admin/security/auth-logs?${params.toString()}`,
      {},
      token ? { authToken: token } : {},
    )
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error || "Unable to load auth logs");
        return body;
      })
      .then((body) => {
        if (!mounted) return;
        setLogs(body?.data || []);
        setTotal(body?.total || 0);
        setError("");
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e.message || "Unable to load auth logs");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [user, token, eventType, search, page]);

  const totalPages = Math.max(1, Math.ceil(total / 40));

  return (
    <div
      style={{ maxWidth: 1200, margin: "0 auto", padding: "42px 24px 70px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 20,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <p
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: T.gold,
              margin: "0 0 6px",
            }}
          >
            Admin Console
          </p>
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 36,
              color: T.ink,
              margin: 0,
            }}
          >
            Security Audit Logs
          </h1>
          <p style={{ margin: "10px 0 0", color: T.muted }}>
            Track login failures, lockouts, refresh events, and logout activity.
          </p>
        </div>
        <button
          onClick={() => navigate("/home")}
          style={{
            background: T.surface,
            border: `2px solid ${T.gold}`,
            color: T.ink,
            borderRadius: 12,
            padding: "10px 16px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Exit admin view
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "220px 1fr auto",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <select
          value={eventType}
          onChange={(e) => {
            setPageNum(1);
            setEventType(e.target.value);
          }}
          style={{
            padding: "10px 12px",
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            background: T.surface,
            color: T.ink,
          }}
        >
          <option value="">All events</option>
          <option value="login_success">Login success</option>
          <option value="login_failed_unknown_email">
            Login unknown email
          </option>
          <option value="login_failed_bad_password">Login bad password</option>
          <option value="login_blocked_locked">Login blocked</option>
          <option value="refresh_success">Refresh success</option>
          <option value="refresh_failed_invalid">Refresh invalid</option>
          <option value="logout">Logout</option>
          <option value="register_success">Register</option>
        </select>
        <input
          value={search}
          onChange={(e) => {
            setPageNum(1);
            setSearch(e.target.value);
          }}
          placeholder="Search email, IP, or user"
          style={{
            padding: "10px 12px",
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            color: T.ink,
            background: T.surface,
          }}
        />
        <div
          style={{ alignSelf: "center", color: T.muted, fontSize: 13 }}
        >
          {total} events
        </div>
      </div>

      <div
        style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "180px 210px 1fr 160px 220px",
            gap: 8,
            padding: "12px 16px",
            borderBottom: `1px solid ${T.border}`,
            background: T.goldLight,
            color: T.ink,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".06em",
          }}
        >
          <span>Time</span>
          <span>Event</span>
          <span>User</span>
          <span>IP</span>
          <span>Details</span>
        </div>

        {loading ? (
          <div style={{ padding: 28, color: T.muted }}>
            Loading audit logs…
          </div>
        ) : error ? (
          <div style={{ padding: 28, color: DANGER }}>{error}</div>
        ) : logs.length === 0 ? (
          <div style={{ padding: 28, color: T.muted }}>
            No records matched your filters.
          </div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              style={{
                display: "grid",
                gridTemplateColumns: "180px 210px 1fr 160px 220px",
                gap: 8,
                padding: "12px 16px",
                borderBottom: `1px solid ${T.border}`,
                fontSize: 13,
                color: T.ink,
              }}
            >
              <span>
                {formatDate(log.created_at)}{" "}
                {new Date(log.created_at).toLocaleTimeString("en-GB")}
              </span>
              <span style={{ fontWeight: 700, color: T.gold }}>
                {log.event_type}
              </span>
              <span>
                {log.first_name
                  ? `${log.first_name} ${log.last_name || ""}`.trim()
                  : log.email || "Unknown user"}
              </span>
              <span>{log.ip_address || "—"}</span>
              <span
                style={{
                  color: T.muted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {typeof log.details === "string"
                  ? log.details
                  : log.details
                    ? JSON.stringify(log.details)
                    : "—"}
              </span>
            </div>
          ))
        )}
      </div>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          onClick={() => setPageNum((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            color: T.ink,
            borderRadius: 10,
            padding: "8px 12px",
            cursor: page <= 1 ? "default" : "pointer",
            opacity: page <= 1 ? 0.5 : 1,
          }}
        >
          Previous
        </button>
        <span style={{ color: T.muted, fontSize: 13 }}>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => setPageNum((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            color: T.ink,
            borderRadius: 10,
            padding: "8px 12px",
            cursor: page >= totalPages ? "default" : "pointer",
            opacity: page >= totalPages ? 0.5 : 1,
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
}
